<template>
  <div v-if="visible" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="visible"
        class="fixed inset-0 transition-opacity"
        @click="handleClose"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-[90vw] max-w-[90vw] mx-auto">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-lg font-medium text-gray-900">同步客户</h3>
            <p class="mt-1 text-sm text-gray-500">
              从邮件账户 "<span class="font-medium text-gray-800">{{ account?.email }}</span>" 中提取客户信息
            </p>
          </div>
          <button
            @click="handleClose"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- 表单内容 -->
        <form @submit.prevent="handleSync" class="space-y-4">
          <!-- 提示信息 -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div class="flex">
              <svg class="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div class="text-sm text-blue-800">
                <p class="font-medium mb-1">同步说明：</p>
                <ul class="list-disc list-inside space-y-0.5 text-xs">
                  <li>系统将从已同步的邮件中提取发件人和收件人信息</li>
                  <li>如果还没有同步邮件，请先邮件管理-同步邮件后操作</li>
                  <li>可以设置过滤条件，只提取符合条件的联系人</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- 标签选择 -->
          <div>
            <div class="flex items-center justify-between mb-3">
              <label class="text-sm font-medium text-gray-700">客户标签（可选）</label>
              <input
                v-model="tagSearchQuery"
                type="text"
                placeholder="搜索标签..."
                class="w-32 h-7 px-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div class="max-h-40 overflow-y-auto pr-2">
              <div v-if="filteredTags.length > 0" class="flex flex-wrap gap-2">
                <label v-for="tag in filteredTags" :key="tag.id" class="cursor-pointer">
                  <input 
                    type="checkbox" 
                    :value="tag.id"
                    v-model="selectedTagIds"
                    class="sr-only" 
                  />
                  <span 
                    class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full transition-colors"
                    :class="selectedTagIds.includes(tag.id) 
                      ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                      : 'bg-gray-100 text-gray-800 border border-gray-300 hover:bg-gray-200'"
                    :style="selectedTagIds.includes(tag.id) 
                      ? { backgroundColor: tag.background_color || '#DBEAFE', color: tag.text_color || '#1E40AF' } 
                      : {}"
                  >
                    {{ tag.name }}
                  </span>
                </label>
              </div>
              <div v-else class="text-center py-4 text-sm text-gray-400">
                {{ tagSearchQuery ? '未找到匹配的标签' : '暂无可用标签' }}
              </div>
            </div>
          </div>
          
          <!-- 分组选择 -->
          <div>
            <div class="flex items-center justify-between mb-3">
              <label class="text-sm font-medium text-gray-700">
                客户分组 <span class="text-red-500">*</span>
              </label>
              <input
                v-model="groupSearchQuery"
                type="text"
                placeholder="搜索分组..."
                class="w-32 h-7 px-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div class="max-h-40 overflow-y-auto pr-2">
              <div v-if="filteredGroups.length > 0" class="flex flex-wrap gap-2">
                <label v-for="group in filteredGroups" :key="group.id" class="cursor-pointer">
                  <input 
                    type="checkbox" 
                    :value="group.id"
                    v-model="selectedGroupIds"
                    class="sr-only" 
                  />
                  <span 
                    class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full transition-colors"
                    :class="selectedGroupIds.includes(group.id) 
                      ? 'bg-green-100 text-green-800 border border-green-300' 
                      : 'bg-gray-100 text-gray-800 border border-gray-300 hover:bg-gray-200'"
                  >
                    {{ group.name }}
                    <span v-if="group.description" class="ml-1 text-gray-500">· {{ group.description }}</span>
                  </span>
                </label>
              </div>
              <div v-else class="text-center py-4 text-sm text-gray-400">
                {{ groupSearchQuery ? '未找到匹配的分组' : '暂无可用分组' }}
              </div>
            </div>
            <p v-if="selectedGroupIds.length === 0" class="text-xs text-red-500 mt-2">请至少选择一个分组</p>
          </div>

          <!-- 姓名过滤 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">姓名排除（可选）</label>
            <input
              v-model="form.nameFilters"
              type="text"
              :disabled="syncing"
              placeholder="输入要排除的关键词，多个用英文逗号隔开，如: noreply,no-reply"
              class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
            <p class="mt-1 text-xs text-gray-500">排除包含这些关键词的联系人姓名（黑名单模式）</p>
          </div>

          <!-- 邮箱过滤 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">邮箱排除（可选）</label>
            <input
              v-model="form.emailFilters"
              type="text"
              :disabled="syncing"
              placeholder="输入要排除的关键词，多个用英文逗号隔开，如: noreply@,@spam.com"
              class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
            <p class="mt-1 text-xs text-gray-500">排除包含这些关键词的邮箱地址（黑名单模式）</p>
          </div>

          <!-- 更新选项 -->
          <div class="flex items-center">
            <input
              id="update-existing"
              v-model="form.updateExisting"
              type="checkbox"
              :disabled="syncing"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label for="update-existing" class="ml-2 block text-sm text-gray-700">
              更新已存在的客户信息（只更新空字段）
            </label>
          </div>

          <!-- 包含发件箱选项 -->
          <div class="flex items-center">
            <input
              id="include-sent"
              v-model="form.includeSent"
              type="checkbox"
              :disabled="syncing"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label for="include-sent" class="ml-2 block text-sm text-gray-700">
              包含发件箱邮件（提取收件人信息）
            </label>
          </div>
        </form>

        <!-- 操作按钮 -->
        <div class="mt-6 flex justify-end space-x-3">
          <button
            @click="handleClose"
            :disabled="syncing"
            type="button"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            取消
          </button>
          <button
            @click="handleSync"
            :disabled="syncing || selectedGroupIds.length === 0"
            type="button"
            class="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg v-if="syncing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ syncing ? '同步' : '同步' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  account: {
    type: Object,
    default: null
  },
  tags: {
    type: Array,
    default: () => []
  },
  groups: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'sync'])

// 表单数据
const form = ref({
  nameFilters: '',
  emailFilters: '',
  updateExisting: false,
  includeSent: true
})

// 选中的标签和分组ID数组
const selectedTagIds = ref([])
const selectedGroupIds = ref([])

// 搜索关键词
const tagSearchQuery = ref('')
const groupSearchQuery = ref('')

// 同步状态
const syncing = ref(false)

// 过滤后的标签
const filteredTags = computed(() => {
  if (!tagSearchQuery.value) {
    return props.tags || []
  }
  const query = tagSearchQuery.value.toLowerCase()
  return (props.tags || []).filter(tag => 
    tag.name.toLowerCase().includes(query)
  )
})

// 过滤后的分组
const filteredGroups = computed(() => {
  if (!groupSearchQuery.value) {
    return props.groups || []
  }
  const query = groupSearchQuery.value.toLowerCase()
  return (props.groups || []).filter(group => 
    group.name.toLowerCase().includes(query) ||
    (group.description && group.description.toLowerCase().includes(query))
  )
})

// 关闭模态框
const handleClose = () => {
  if (!syncing.value) {
    emit('close')
  }
}

// 开始同步
const handleSync = () => {
  // 验证：必须选择至少一个分组
  if (selectedGroupIds.value.length === 0) {
    return
  }
  
  syncing.value = true
  
  // 构建同步选项
  const syncOptions = {
    include_sent: form.value.includeSent,
    update_existing: form.value.updateExisting,
    tag_ids: selectedTagIds.value.length > 0 ? selectedTagIds.value : null,
    group_ids: selectedGroupIds.value,  // 必填，不再需要判断 length
    name_filters: form.value.nameFilters ? form.value.nameFilters.split(',').map(s => s.trim()).filter(Boolean) : null,
    email_filters: form.value.emailFilters ? form.value.emailFilters.split(',').map(s => s.trim()).filter(Boolean) : null
  }
  
  emit('sync', syncOptions)
}

// 监听visible变化，重置表单
watch(() => props.visible, (newVal) => {
  if (newVal) {
    // 重置表单
    form.value = {
      nameFilters: '',
      emailFilters: '',
      updateExisting: false,
      includeSent: true
    }
    selectedTagIds.value = []
    selectedGroupIds.value = []
    tagSearchQuery.value = ''
    groupSearchQuery.value = ''
  } else {
    syncing.value = false
  }
})

// 暴露方法供父组件调用
defineExpose({
  setSyncing: (value) => {
    syncing.value = value
  }
})
</script>


